// A text input that offers the values already recorded for the same field.
//
// Distributor, category and price type were typed by hand in three separate
// forms writing `bv_site_network`, which is why `distributorsMatch` carries an
// alias table to reconcile "Ausgrid" with "Ausgrid Networks", and why the site
// page needs case- and whitespace-tolerant code matching at all. Offering back
// what the org already uses is what stops the next spelling being invented.
//
// Deliberately a native `<datalist>` rather than the Command/Popover combobox
// used elsewhere. Two reasons: a genuinely new value must still be typeable —
// these are not closed vocabularies, and a picker that refuses an unlisted
// distributor would block the first site on a new network — and a Popover
// nested inside a Dialog fights the dialog's focus trap. The datalist is a
// suggestion, not a constraint, which is exactly the intent here.

import { useId } from 'react';
import { Input } from '../../../../foundation/altzero_base/base/components/ui/input';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Values already in use for this field. Deduped and sorted by the caller. */
  options: string[];
  id?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Uppercase as the user types — for tariff codes, which are stored uppercase. */
  uppercase?: boolean;
};

export function SuggestInput({
  value, onChange, options, id, placeholder, className, disabled, uppercase = false,
}: Props) {
  // The list id must be unique per instance: several of these render side by
  // side in the same dialog, and a shared id would point them all at one list.
  const listId = `suggest-${useId()}`;

  return (
    <>
      <Input
        id={id}
        list={listId}
        className={className}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(uppercase ? e.target.value.toUpperCase() : e.target.value)}
      />
      <datalist id={listId}>
        {options.map((option) => <option key={option} value={option} />)}
      </datalist>
    </>
  );
}
